import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useState } from "react";
import { Alert, Badge, Button, Container, Form, Spinner, Table } from "react-bootstrap";
import { useSuperAdminInvoices } from "../hooks";

function formatCOP(amount) {
  if (amount == null) return "—";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(amount);
}

const STATUS_VARIANTS = {
  PAID: "success",
  PENDING: "warning",
  OVERDUE: "danger",
};

const COLUMNS = [
  {
    id: "company",
    header: "Academia",
    accessorFn: (row) => row.company?.name ?? "—",
  },
  { accessorKey: "period", header: "Periodo" },
  {
    accessorKey: "amount",
    header: "Monto",
    cell: ({ getValue }) => formatCOP(getValue()),
  },
  {
    accessorKey: "tax_amount",
    header: "IVA (19%)",
    cell: ({ getValue }) => formatCOP(getValue()),
  },
  {
    id: "total",
    header: "Total",
    accessorFn: (row) => (row.amount ?? 0) + (row.tax_amount ?? 0),
    cell: ({ getValue }) => formatCOP(getValue()),
  },
  {
    accessorKey: "status",
    header: "Estado",
    cell: ({ getValue }) => {
      const v = getValue();
      return <Badge bg={STATUS_VARIANTS[v] ?? "secondary"}>{v}</Badge>;
    },
  },
];

// TASK-22: esta página NO tiene gating por rol a propósito. La matriz de staff
// solo define `invoices:read` (PLATFORM_ADMIN/FINANCE/READ_ONLY) y el acceso ya
// se decide en la pestaña del SuperAdminLayout: quien no puede leer facturas no
// llega aquí. El único control es un CSV construido en cliente a partir de datos
// que el usuario ya descargó — no es una mutación y no necesita permiso extra.
function InvoicesPage() {
  const { data: invoices, isLoading, isError } = useSuperAdminInvoices();
  const [globalFilter, setGlobalFilter] = useState("");

  const table = useReactTable({
    data: invoices ?? [],
    columns: COLUMNS,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 25 } },
  });

  // CSV download — placeholder para TASK-19
  function downloadCsv() {
    // # TASK-19: generar CSV real con los datos de facturas
    const rows = (invoices ?? []).map((inv) =>
      [
        inv.company?.name ?? "",
        inv.period ?? "",
        inv.amount ?? 0,
        inv.tax_amount ?? 0,
        (inv.amount ?? 0) + (inv.tax_amount ?? 0),
        inv.status ?? "",
      ].join(",")
    );
    const csv = ["Academia,Periodo,Monto,IVA,Total,Estado", ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "facturas.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  if (isLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner aria-label="Cargando facturas" />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container className="py-4">
        <Alert variant="danger">No se pudo cargar las facturas.</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h4 className="mb-0">Facturas</h4>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={downloadCsv}
          disabled={!invoices?.length}
          aria-label="Descargar facturas como CSV"
        >
          ↓ Exportar CSV
        </Button>
      </div>

      <Form.Control
        className="mb-3"
        style={{ maxWidth: "340px" }}
        placeholder="Buscar academia..."
        value={globalFilter}
        onChange={(e) => setGlobalFilter(e.target.value)}
        aria-label="Buscar factura por academia"
      />

      <div className="table-responsive">
        <Table hover bordered size="sm" className="align-middle">
          <thead className="table-light">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="text-center text-muted py-3">
                  No hay facturas registradas.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <div className="d-flex align-items-center gap-2 mt-2">
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          aria-label="Página anterior"
        >
          ‹ Anterior
        </Button>
        <span className="small text-muted">
          Página {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
        </span>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          aria-label="Página siguiente"
        >
          Siguiente ›
        </Button>
      </div>
    </Container>
  );
}

export { InvoicesPage };
